import React, { useState, useEffect, useCallback } from 'react';
import apiV2 from '../utils/apiV2';
import { toast } from 'sonner';
import { CreditCard, ShieldCheck, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const REASONS = [
  { id: 'gratuity', label: 'Captain gratuity' },
  { id: 'damage', label: 'Damage' },
  { id: 'fuel', label: 'Fuel' },
  { id: 'other', label: 'Other' },
];

const fmt = (n, cur = 'USD') =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: cur || 'USD' }).format(Number(n) || 0);

export const ReconcilePaymentSection = ({ orgId, trip }) => {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [charging, setCharging] = useState(false);

  const load = useCallback(async () => {
    if (!orgId || !trip?.id) return;
    try {
      const res = await apiV2.get(`/organizations/${orgId}/bookings/${trip.id}/card-on-file`);
      setState(res.data);
    } catch (e) {
      setState(null);
    } finally {
      setLoading(false);
    }
  }, [orgId, trip?.id]);

  useEffect(() => { load(); }, [load]);

  if (loading) return null;
  if (!state) return null;

  const amt = parseFloat(amount) || 0;
  const overCap = amt > (state.remainingCap || 0) + 0.001;
  const formValid = amt >= 0.5 && reason && !overCap;

  const handleCharge = async () => {
    if (charging) return; // re-entry guard — never fire twice
    setCharging(true);
    try {
      const res = await apiV2.post(`/organizations/${orgId}/bookings/${trip.id}/charge-card`, {
        amount: amt,
        reason_category: reason,
        note: note || null,
      }, { timeout: 65000 });
      toast.success(`Charged ${fmt(amt, state.currency)} to card on file${res.data.customerNotified ? ' — customer notified' : ''}`);
      setAmount(''); setReason(''); setNote(''); setConfirming(false);
      load();
    } catch (e) {
      const msg = e.code === 'ECONNABORTED'
        ? 'Request timed out — check the charge history below (and Stripe) before retrying to avoid a double charge.'
        : (e.response?.data?.detail || 'Charge failed');
      toast.error(msg, { duration: 8000 });
      setConfirming(false);
      load(); // refresh so any failed/pending attempt shows in history
    } finally {
      setCharging(false);
    }
  };

  return (
    <div className="border border-slate-200 rounded-xl p-4 bg-white space-y-3" data-testid="reconcile-payment-section">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-slate-600" />
          <p className="text-sm font-semibold text-gray-800">Reconcile Payment (card on file)</p>
        </div>
        {state.hasCard ? (
          <span className="text-xs font-medium bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full" data-testid="card-on-file-badge">
            {(state.cardBrand || 'Card').toUpperCase()} •••• {state.cardLast4}
          </span>
        ) : (
          <span className="text-xs text-gray-400" data-testid="no-card-on-file">No card on file</span>
        )}
      </div>

      {!state.hasCard && (
        <p className="text-xs text-gray-500">
          Cards are saved automatically on new checkouts. This booking was paid before card saving was enabled
          (or paid without a card), so post-trip charges aren't available — use a payment link instead.
        </p>
      )}

      {state.hasCard && (
        <>
          <div className="flex items-center gap-x-4 gap-y-1 flex-wrap text-xs text-gray-500">
            <span>Charged so far: <strong className="text-gray-700">{fmt(state.chargedTotal, state.currency)}</strong> of {fmt(state.chargeCap, state.currency)} max</span>
            {state.windowEndsAt && (
              <span className={state.windowOpen ? '' : 'text-red-600 font-medium'}>
                {state.windowOpen ? `Chargeable until ${new Date(state.windowEndsAt).toLocaleDateString()}` : 'Charge window closed'}
              </span>
            )}
          </div>

          {state.canCharge ? (
            <div className="space-y-2.5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Input
                  type="number" min="0.5" step="0.01" placeholder="Amount"
                  value={amount} onChange={(e) => { setAmount(e.target.value); setConfirming(false); }}
                  className="h-11 bg-white" data-testid="reconcile-amount-input"
                />
                <Select value={reason} onValueChange={(v) => { setReason(v); setConfirming(false); }}>
                  <SelectTrigger className="h-11 bg-white" data-testid="reconcile-reason-select">
                    <SelectValue placeholder="Reason" />
                  </SelectTrigger>
                  <SelectContent>
                    {REASONS.map((r) => (
                      <SelectItem key={r.id} value={r.id} data-testid={`reconcile-reason-${r.id}`}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Textarea
                placeholder="Note (optional — shown to the customer)" rows={2}
                value={note} onChange={(e) => setNote(e.target.value)}
                className="bg-white text-sm" data-testid="reconcile-note-input"
              />
              {overCap && (
                <p className="text-xs text-red-600 flex items-center gap-1">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Max remaining charge for this booking is {fmt(state.remainingCap, state.currency)} (capped at the booking total).
                </p>
              )}
              {!confirming ? (
                <Button
                  className="w-full h-11" disabled={!formValid}
                  onClick={() => setConfirming(true)}
                  data-testid="reconcile-charge-btn"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Charge {amt >= 0.5 ? fmt(amt, state.currency) : 'card'}
                </Button>
              ) : (
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-xs text-amber-800 flex-1">
                    Charge <strong>{fmt(amt, state.currency)}</strong> to {(state.cardBrand || 'card').toUpperCase()} ••••{state.cardLast4}?
                    The customer will be notified by email &amp; SMS.
                  </p>
                  <div className="flex gap-2">
                    <Button size="sm" className="flex-1 sm:flex-none h-10 sm:h-9" onClick={handleCharge} disabled={charging} data-testid="reconcile-confirm-btn">
                      {charging ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm'}
                    </Button>
                    <Button size="sm" variant="outline" className="flex-1 sm:flex-none h-10 sm:h-9" onClick={() => setConfirming(false)} disabled={charging} data-testid="reconcile-cancel-btn">
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-xs text-gray-500 flex items-center gap-1" data-testid="reconcile-no-permission">
              <ShieldCheck className="w-3.5 h-3.5" />
              {!state.hasPermission
                ? 'You need the "Reconcile payments" permission to charge this card.'
                : !state.windowOpen
                  ? 'The 14-day post-trip charge window has closed.'
                  : 'Charging unavailable for this booking.'}
            </p>
          )}

          {state.charges?.length > 0 && (
            <div className="pt-2 border-t space-y-1.5" data-testid="reconcile-charge-history">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Charge history</p>
              {state.charges.map((c) => (
                <div key={c.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-0.5 text-xs bg-slate-50 rounded-md border border-slate-100 px-2.5 py-1.5">
                  <span className={c.status === 'succeeded' ? 'text-gray-700' : c.status === 'pending' ? 'text-amber-600' : 'text-red-600'}>
                    <strong>{fmt(c.amount, c.currency)}</strong> · {c.reasonLabel}
                    {c.status === 'pending' && ' · PROCESSING…'}
                    {c.status === 'failed' && ` · FAILED${c.errorMessage ? `: ${c.errorMessage}` : ''}`}
                  </span>
                  <span className="text-gray-400 shrink-0">
                    {c.chargedByName} · {c.createdAt ? new Date(c.createdAt).toLocaleDateString() : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ReconcilePaymentSection;
